import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Gift } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { ConditionBadge } from "@/components/ConditionBadge";
import { bundleDiscount, nextBundleTier } from "@/lib/bundle";

export function CartSheet() {
  const { items, setQty, remove, subtotal, open, setOpen, count } = useCart();
  const navigate = useNavigate();
  const bundle = bundleDiscount(subtotal, count);
  const next = nextBundleTier(count);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md p-0 border-l-2 border-ink flex flex-col rounded-none" data-testid="cart-sheet">
        <SheetHeader className="p-6 border-b-2 border-ink text-left">
          <SheetTitle className="font-heading font-black text-2xl tracking-tighter flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> YOUR CART ({count})
          </SheetTitle>
          <SheetDescription className="sr-only">Items currently in your shopping cart</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center font-mono text-sm text-subtle">
              Cart is empty. Browse the catalog to add liquidation stock.
            </div>
          ) : (
            items.map((it) => (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex gap-3 p-4 border-b border-surface2">
                <img src={it.image} alt={it.name} className="h-20 w-20 object-cover border border-ink bg-surface" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm leading-tight line-clamp-2">{it.name}</div>
                  <div className="mt-1"><ConditionBadge condition={it.condition} /></div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center border border-ink">
                      <button data-testid={`cart-dec-${it.id}`} onClick={() => setQty(it.id, it.qty - 1)} className="px-2 py-1 hover:bg-ink hover:text-paper transition-colors"><Minus className="h-3 w-3" /></button>
                      <span className="px-3 font-mono text-sm">{it.qty}</span>
                      <button data-testid={`cart-inc-${it.id}`} onClick={() => setQty(it.id, it.qty + 1)} className="px-2 py-1 hover:bg-ink hover:text-paper transition-colors"><Plus className="h-3 w-3" /></button>
                    </div>
                    <div className="font-mono font-bold text-sm">{money(it.our_price * it.qty)}</div>
                  </div>
                </div>
                <button data-testid={`cart-remove-${it.id}`} onClick={() => remove(it.id)} className="text-subtle hover:text-danger transition-colors self-start"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t-2 border-ink p-6 space-y-3">
            {bundle.pct > 0 && (
              <div data-testid="cart-bundle-applied" className="flex items-center gap-2 border-2 border-success bg-success/10 p-2 font-mono text-xs">
                <Gift className="h-4 w-4 text-success shrink-0" />
                <span><b>Bundle savings</b> · {bundle.pct}% off applied ({count} items)</span>
              </div>
            )}
            {next && (
              <div data-testid="cart-bundle-nudge" className="flex items-center gap-2 border border-dashed border-ink/40 p-2 font-mono text-[11px] text-subtle">
                <Gift className="h-3.5 w-3.5 shrink-0" />
                <span>Add <b className="text-ink">{next.add_qty} more</b> item{next.add_qty > 1 ? "s" : ""} to save <b className="text-ink">{next.pct}%</b> on your order</span>
              </div>
            )}
            <div className="flex justify-between font-mono text-sm">
              <span className="text-subtle uppercase tracking-wider">Subtotal</span>
              <span className="font-bold">{money(subtotal)}</span>
            </div>
            {bundle.amount > 0 && (
              <div className="flex justify-between font-mono text-sm text-success">
                <span className="uppercase tracking-wider">Bundle -{bundle.pct}%</span>
                <span className="font-bold">-{money(bundle.amount)}</span>
              </div>
            )}
            <p className="font-mono text-[11px] text-subtle">Shipping &amp; Florida tax calculated at checkout.</p>
            <button
              data-testid="checkout-nav-button"
              onClick={() => { setOpen(false); navigate("/checkout"); }}
              className="w-full bg-ink text-paper h-12 font-mono text-sm uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform"
            >
              Proceed to Checkout →
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
